import { buyerportalApi } from '..';
import { callApi, getResponseError } from '../utils';
import {
  GetForSquareBuyerPersonRequest,
  GetForSquareBuyerPersonResponse,
} from 'rpc/model/squareup/rewards/rewardCode/buyer_service';
import rewardCodeClient from 'rpc/rewardCodeClient';
import rewardClient from 'rpc/rewardClient';
import { RewardCode } from 'rpc/model/squareup/rewards/rewardCode/model';
import {
  GetActiveBalancesV2Request,
  GetActiveBalancesV2Response,
  SendReferralRequest,
  SendReferralResponse,
} from 'rpc/model/squareup/rewards/service';
import { RequestStatus } from '../../../rpc/model/squareup/customers/request';
import { logReferralError } from 'routes/refer/utils/error';
import BalancesOfReward = GetActiveBalancesV2Response.BalancesOfReward;

export const SEND_REWARD_RESPONSE_ERRORS = {
  DUPLICATE_REFERRAL: 'DUPLICATE_REFERRAL',
  EXISTING_MERCHANT: 'EXISTING_MERCHANT',
  INVALID_REWARD_CODE: 'INVALID_REWARD_CODE',
  MISSING_DATA: 'MISSING_DATA',
  UNKNOWN: 'UNKNOWN',
};

export const rewardsRpcs = buyerportalApi.injectEndpoints({
  endpoints: (builder) => ({
    getActiveBalancesV2: builder.query<BalancesOfReward[], void>({
      async queryFn() {
        try {
          const response = await callApi<GetActiveBalancesV2Response>(() =>
            rewardClient.getActiveBalancesV2(
              GetActiveBalancesV2Request.create()
            )
          );

          if (!response.data || !response.data?.mobileStatus?.success) {
            logReferralError(new Error('getActiveBalancesV2 failed'), {
              hasData: !!response.data,
              mobileStatusSuccess: response.data?.mobileStatus?.success,
              rpc: 'getActiveBalancesV2',
            });

            return getResponseError({
              errors: [{ details: 'Unknown error' }],
              status: RequestStatus.STATUS_UNKNOWN,
            });
          }

          return { data: response.data?.balancesOfRewards };
        } catch (error) {
          logReferralError(error as Error, { rpc: 'getActiveBalancesV2' });
          throw error;
        }
      },
    }),
    getForSquareBuyerPerson: builder.query<RewardCode, void>({
      async queryFn() {
        try {
          const response = await callApi<GetForSquareBuyerPersonResponse>(() =>
            rewardCodeClient.getForSquareBuyerPerson(
              GetForSquareBuyerPersonRequest.create()
            )
          );

          if (
            !response.data ||
            response.data.status !==
              GetForSquareBuyerPersonResponse.Status.SUCCESS
          ) {
            logReferralError(new Error('getForSquareBuyerPerson failed'), {
              hasData: !!response.data,
              rpc: 'getForSquareBuyerPerson',
              status: response.data?.status,
            });

            return getResponseError();
          }

          return {
            data: response.data?.rewardCode,
          };
        } catch (error) {
          logReferralError(error as Error, { rpc: 'getForSquareBuyerPerson' });
          throw error;
        }
      },
    }),
    sendReferral: builder.mutation<boolean, SendReferralRequest>({
      async queryFn(request: SendReferralRequest) {
        try {
          const response = await callApi<SendReferralResponse>(() =>
            rewardClient.sendReferral(request)
          );

          const sendReferralStatus =
            response?.data?.sendReferralStatus ??
            SendReferralResponse.SendReferralStatus.UNKNOWN;

          if (
            sendReferralStatus ===
            SendReferralResponse.SendReferralStatus.EXISTING_MERCHANT
          ) {
            throw SEND_REWARD_RESPONSE_ERRORS.EXISTING_MERCHANT;
          }

          if (
            sendReferralStatus ===
            SendReferralResponse.SendReferralStatus.DUPLICATE_REFERRAL
          ) {
            throw SEND_REWARD_RESPONSE_ERRORS.DUPLICATE_REFERRAL;
          }

          if (
            sendReferralStatus ===
            SendReferralResponse.SendReferralStatus.MISSING_DATA
          ) {
            throw SEND_REWARD_RESPONSE_ERRORS.MISSING_DATA;
          }

          if (
            sendReferralStatus ===
            SendReferralResponse.SendReferralStatus.REWARD_CODE_NOT_FOUND
          ) {
            throw SEND_REWARD_RESPONSE_ERRORS.INVALID_REWARD_CODE;
          }

          if (
            sendReferralStatus !==
            SendReferralResponse.SendReferralStatus.SUCCESS
          ) {
            const error = new Error(
              `Unexpected sendReferral status: ${sendReferralStatus}`
            );
            logReferralError(error, {
              rpc: 'sendReferral',
              sendReferralStatus,
            });
            throw SEND_REWARD_RESPONSE_ERRORS.UNKNOWN;
          }

          return {
            data: response.data,
          };
        } catch (error) {
          const isExpectedError =
            typeof error === 'string' &&
            Object.values(SEND_REWARD_RESPONSE_ERRORS).includes(error);

          if (!isExpectedError) {
            logReferralError(error as Error, { rpc: 'sendReferral' });
          }
          throw error;
        }
      },
    }),
  }),
});

export const {
  useGetForSquareBuyerPersonQuery,
  useSendReferralMutation,
  useGetActiveBalancesV2Query,
} = rewardsRpcs;
